import type { DirectusProduct } from '@/lib/directus'
import { levenshteinDistance } from '@/lib/search-utils'

const INTENT_WORDS = new Set([
  'a',
  'all',
  'best',
  'compatible',
  'for',
  'grass',
  'lawn',
  'lawns',
  'my',
  'product',
  'products',
  'safe',
  'season',
  'the',
  'to',
  'use',
  'with',
  'works',
])

function normalize(value: unknown): string {
  return String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

function list(value: string[] | string | null | undefined): string[] {
  if (Array.isArray(value)) return value
  if (typeof value === 'string') return value.split(',').map((entry) => entry.trim()).filter(Boolean)
  return []
}

function queryTokens(query: string): string[] {
  const normalized = normalize(query)
  const meaningful = normalized
    .split(/\s+/)
    .filter((token) => token.length > 1 && !INTENT_WORDS.has(token))

  return meaningful.length > 0 ? meaningful : normalized.split(/\s+/).filter(Boolean)
}

function tokenMatches(token: string, field: string): boolean {
  if (field.includes(token)) return true
  if (token.length < 4) return false

  return field.split(/\s+/).some((word) => {
    if (word.length < 4) return false
    const allowedDistance = Math.min(2, Math.floor(Math.max(word.length, token.length) * 0.25))
    return levenshteinDistance(token, word) <= allowedDistance
  })
}

function scoreProduct(product: DirectusProduct, query: string): number | null {
  const tokens = queryTokens(query)
  if (tokens.length === 0) return null

  const fields = {
    name: normalize(product.name),
    description: normalize(product.description),
    category: normalize(product.category),
    timing: normalize(product.timing),
    grasses: normalize(list(product.grass_types).filter((value) => value !== 'any').join(' ')),
    seasons: normalize(list(product.season_affinity).filter((value) => value !== 'any').join(' ')),
  }

  let score = 0
  for (const token of tokens) {
    let tokenScore = 0
    if (tokenMatches(token, fields.name)) tokenScore = Math.max(tokenScore, 40)
    if (tokenMatches(token, fields.grasses)) tokenScore = Math.max(tokenScore, 32)
    if (tokenMatches(token, fields.category)) tokenScore = Math.max(tokenScore, 24)
    if (tokenMatches(token, fields.timing)) tokenScore = Math.max(tokenScore, 22)
    if (tokenMatches(token, fields.seasons)) tokenScore = Math.max(tokenScore, 20)
    if (tokenMatches(token, fields.description)) tokenScore = Math.max(tokenScore, 16)

    if (tokenScore === 0) return null
    score += tokenScore
  }

  const normalizedQuery = normalize(query)
  if (normalizedQuery && fields.name.includes(normalizedQuery)) score += 100
  if (product.featured) score += 3
  if (product.best_seller) score += 2

  return score
}

/**
 * Search the complete product catalog using only fields currently stored in
 * Directus. Results must match every meaningful query token, then rank name and
 * explicit grass matches ahead of descriptive mentions.
 */
export function searchProducts(products: DirectusProduct[], query: string): DirectusProduct[] {
  return products
    .map((product, index) => ({ product, index, score: scoreProduct(product, query) }))
    .filter((entry): entry is { product: DirectusProduct; index: number; score: number } => entry.score !== null)
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .map(({ product }) => product)
}

function titleFromSlug(value: string): string {
  return value
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

export function productGrassMatchLabel(product: DirectusProduct): string | null {
  const grasses = list(product.grass_types)
  if (grasses.includes('any')) return null

  const namedGrasses = grasses.filter(Boolean).map(titleFromSlug)
  if (namedGrasses.length > 0) {
    const visible = namedGrasses.slice(0, 2)
    const remainder = namedGrasses.length - visible.length
    return `${visible.join(', ')}${remainder > 0 ? ` +${remainder}` : ''}`
  }

  const seasons = list(product.season_affinity).filter((value) => value && value !== 'any')
  if (seasons.length === 1) return `${titleFromSlug(seasons[0])}-season lawns`
  if (seasons.length > 1) return seasons.map(titleFromSlug).join(', ')

  return null
}
